import React, { useState } from 'react'
import { Icon, SideBarItem } from '@deque/cauldron-react'
import { Link } from 'gatsby'
import classNames from 'classnames'
import '@deque/cauldron-styles'
import './NavItem.scss'
import { useGatsbyPageContext } from '../../context/GatsbyPageContext'
import { validate as isUuid } from 'uuid'
import { NormalizedNavItem } from '../../types/navdata'
import { useTranslation } from 'react-i18next'

interface NavItemProps {
  pageId: string
  title: string
  items: NormalizedNavItem[]
  ancestors: string[]
  activePageAncestors: string[]
  dismissNav: () => void
  path?: string
}

export default function NavItem(props: NavItemProps) {
  const {
    pageId,
    title,
    items,
    ancestors,
    activePageAncestors,
    dismissNav,
    path,
  } = props
  const {
    productId,
    version,
    locale,
    pageId: activePageId,
  } = useGatsbyPageContext()
  const isActive = activePageId === pageId
  const isAncestorOfActivePage = activePageAncestors.includes(pageId)
  const [expanded, setExpanded] = useState(isActive || isAncestorOfActivePage)
  const { t } = useTranslation()
  // Expand item if it is an ancestor of the active page
  React.useEffect(() => {
    if (isAncestorOfActivePage) {
      setExpanded(true)
    }
  }, [isAncestorOfActivePage])

  return (
    <SideBarItem
      className={classNames(
        'NavItem',
        `NavItem--depth${ancestors.length.toString()}`
      )}
    >
      <div className="NavItem__Title">
        {items.length > 0 && (
          <button
            className={classNames('NavItem__ToggleButton', {
              'NavItem__ToggleButton--expanded': expanded,
            })}
            onClick={() => setExpanded(!expanded)}
            disabled={isAncestorOfActivePage}
          >
            <Icon type={expanded ? 'triangle-down' : 'triangle-right'} />
            <span className="Offscreen">
              {`${expanded ? t('hideChildren') : t('showChildren')} ${title}`}
            </span>
          </button>
        )}
        {typeof path === 'undefined' ? (
          <div
            className="NavItem__TitleText"
            onClick={() => {
              // Don't allow an ancestor of the active page to be collapsed
              if (isAncestorOfActivePage) {
                return
              }
              // If the item has children, mimic the behavior of the expand/collapse button
              if (items.length > 0) {
                setExpanded(!expanded)
              }
            }}
          >
            <span>{title}</span>
          </div>
        ) : (
          <Link
            className="NavItem__TitleText"
            to={path}
            aria-current={isActive ? 'page' : undefined}
            onClick={() => {
              if (items.length > 0) {
                setExpanded(true)
              }
              dismissNav()
            }}
          >
            <span>{title}</span>
          </Link>
        )}
      </div>
      {items.length > 0 && (
        <ul
          className={classNames('NavItem__List', {
            'NavItem__List--expanded': expanded,
          })}
        >
          {items.map(({ id, title, items, ancestors }) => {
            const path =
              isUuid(id) === false
                ? `/${productId}/${version}/${locale}/${id}`
                : undefined
            return (
              <NavItem
                key={id}
                pageId={id}
                path={path}
                title={title}
                items={items}
                ancestors={ancestors}
                activePageAncestors={activePageAncestors}
                dismissNav={dismissNav}
              />
            )
          })}
        </ul>
      )}
    </SideBarItem>
  )
}
